// storage-client.js

/**
 * Gets the auth token from localStorage
 * @returns {string|null} Auth token or null
 */
function getAuthToken() {
    return localStorage.getItem('authToken');
}

/**
 * Gets auth headers for API requests
 * @returns {object} Headers object
 */
function getAuthHeaders() {
    const token = getAuthToken();
    const headers = { "Content-Type": "application/json" };

    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    return headers;
}

const DEFAULT_TIMEOUT_MS = 20000;
// Ask Kai exact-count requests can run an initial generation plus one repair pass.
// Keep the client-side timeout comfortably above the backend's two-attempt budget
// so successful responses are not aborted by the browser first.
const ASK_KAI_TIMEOUT_MS = 95000;
const DEBUG_LOG_LOCALSTORAGE_KEY = "debugLogsEnabled";
const WRITE_QUEUE_COOLDOWN_MS = 300;
const SAVE_DATA_MAX_RETRIES = 2;

// ── Health reporters ───────────────────────────────────────────────────────────
// Tracks consecutive save failures across all KV and docs-API save paths.
// At STORAGE_UNHEALTHY_THRESHOLD failures → dispatches 'storage:unhealthy'.
// First success after unhealthy → dispatches 'storage:healthy'.
// Callers: _postStorageValue (KV path) and saveActiveProjectDocIfDirty (docs API path).
// Do NOT also call from saveTasks()/saveProjects() — they already flow through
// _postStorageValue, so double-calling would inflate the failure count.
let _consecutiveFailures = 0;
const STORAGE_UNHEALTHY_THRESHOLD = 1;

export function reportSaveSuccess() {
    const wasUnhealthy = _consecutiveFailures >= STORAGE_UNHEALTHY_THRESHOLD;
    _consecutiveFailures = 0;
    if (wasUnhealthy) {
        window.dispatchEvent(new CustomEvent('storage:healthy'));
    }
}

export function reportSaveFailure() {
    _consecutiveFailures++;
    if (_consecutiveFailures === STORAGE_UNHEALTHY_THRESHOLD) {
        window.dispatchEvent(new CustomEvent('storage:unhealthy'));
    }
}

export function isStorageHealthy() {
    return _consecutiveFailures < STORAGE_UNHEALTHY_THRESHOLD;
}

// ── Write queue ───────────────────────────────────────────────────────────────
// Serialises writes to the same KV key so rapid saves (e.g. "Create Another"
// tasks or quick project creation) do not overlap, and keeps a small cooldown
// between same-key writes to stay within KV's per-key write expectations.
//
// Per-key behaviour:
//   • Each key has at most one in-flight write.
//   • Additional saves for the same key are coalesced into the latest snapshot.
//   • Callers for buffered saves resolve only after that latest snapshot is
//     written, so failures are not hidden behind the first request's promise.
const _writeQueue = new Map();
const _WRITE_QUEUE_EMPTY = Symbol('empty');

function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

async function _flushQueuedWrite(key, writeFn) {
    const entry = _writeQueue.get(key);
    if (!entry || entry.inFlight || entry.next === _WRITE_QUEUE_EMPTY) {
        if (entry && !entry.inFlight && entry.next === _WRITE_QUEUE_EMPTY) {
            _writeQueue.delete(key);
        }
        return;
    }

    entry.inFlight = true;
    const value = entry.next;
    const waiters = entry.waiters.splice(0);
    entry.next = _WRITE_QUEUE_EMPTY;

    try {
        const waitMs = entry.lastStartedAt
            ? Math.max(0, WRITE_QUEUE_COOLDOWN_MS - (Date.now() - entry.lastStartedAt))
            : 0;
        if (waitMs > 0) {
            await delay(waitMs);
        }
        entry.lastStartedAt = Date.now();
        const result = await writeFn(value);
        waiters.forEach(({ resolve }) => resolve(result));
    } catch (error) {
        waiters.forEach(({ reject }) => reject(error));
    } finally {
        entry.inFlight = false;
        if (entry.next !== _WRITE_QUEUE_EMPTY) {
            // Pass the original writeFn into the next batch. This is safe because
            // all saveData() calls for the same key produce an equivalent writeFn
            // (same _postStorageValue closure). If writeFn ever becomes
            // caller-specific, _enqueueWrite would need to store it alongside next.
            void _flushQueuedWrite(key, writeFn);
        } else {
            _writeQueue.delete(key);
        }
    }
}

async function _enqueueWrite(key, value, writeFn) {
    return new Promise((resolve, reject) => {
        let entry = _writeQueue.get(key);
        if (!entry) {
            entry = {
                inFlight: false,
                next: _WRITE_QUEUE_EMPTY,
                waiters: [],
                lastStartedAt: 0
            };
            _writeQueue.set(key, entry);
        }

        entry.next = value;
        entry.waiters.push({ resolve, reject });
        if (!entry.inFlight) {
            void _flushQueuedWrite(key, writeFn);
        }
    });
}

async function _postStorageValue(key, value) {
    let lastError = null;

    for (let attempt = 0; attempt <= SAVE_DATA_MAX_RETRIES; attempt++) {
        const response = await fetchWithRetry(`/api/storage?key=${encodeURIComponent(key)}`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(value),
        });

        if (response.ok) {
            reportSaveSuccess();
            return true;
        }

        if (response.status === 401) {
            window.location.hash = '#login';
            throw new Error('Unauthorized - please login');
        }

        const responseText = await response.text().catch(() => '');
        lastError = new Error(
            `Failed to save data: ${response.status} ${response.statusText}${responseText ? ` - ${responseText}` : ''}`
        );
        const looksRateLimited = response.status === 429 || /429|rate limit/i.test(responseText);
        const retryable = looksRateLimited || response.status >= 500;
        if (attempt < SAVE_DATA_MAX_RETRIES && retryable) {
            await delay(Math.max(WRITE_QUEUE_COOLDOWN_MS, 500 * Math.pow(2, attempt)));
            continue;
        }
        reportSaveFailure();
        throw lastError;
    }

    reportSaveFailure();
    throw lastError || new Error('Failed to save data');
}

function isDebugLogsEnabled() {
    try {
        return localStorage.getItem(DEBUG_LOG_LOCALSTORAGE_KEY) === "true";
    } catch (e) {
        return false;
    }
}

function logFeedbackDebug(message, meta) {
    if (!isDebugLogsEnabled()) return;
    if (meta) {
        console.log(`[feedback-debug] ${message}`, meta);
    } else {
        console.log(`[feedback-debug] ${message}`);
    }
}
async function fetchWithTimeout(resource, options = {}, timeoutMs = DEFAULT_TIMEOUT_MS) {
    const controller = new AbortController();
    const externalSignal = options?.signal;
    let didTimeout = false;
    let abortRelay = null;
    const id = setTimeout(() => {
        didTimeout = true;
        controller.abort();
    }, timeoutMs);
    if (externalSignal) {
        if (externalSignal.aborted) {
            clearTimeout(id);
            throw externalSignal.reason instanceof Error
                ? externalSignal.reason
                : new DOMException('The operation was aborted.', 'AbortError');
        }
        abortRelay = () => controller.abort();
        externalSignal.addEventListener('abort', abortRelay, { once: true });
    }
    try {
        return await fetch(resource, { ...options, signal: controller.signal });
    } catch (error) {
        if (didTimeout) {
            const timeoutError = new Error(`Request timed out after ${timeoutMs}ms`);
            timeoutError.name = 'TimeoutError';
            timeoutError.code = 'TIMEOUT';
            timeoutError.cause = error;
            throw timeoutError;
        }
        throw error;
    } finally {
        clearTimeout(id);
        if (externalSignal && abortRelay) {
            externalSignal.removeEventListener('abort', abortRelay);
        }
    }
}

/**
 * Fetch with automatic retry on network errors (including QUIC protocol errors on slow networks)
 * @param {string} resource - URL to fetch
 * @param {object} options - Fetch options
 * @param {number} timeoutMs - Timeout in milliseconds
 * @param {number} maxRetries - Maximum number of retry attempts
 * @returns {Promise<Response>}
 */
async function fetchWithRetry(resource, options = {}, timeoutMs = DEFAULT_TIMEOUT_MS, maxRetries = 2) {
    // Only retry 5xx on idempotent methods — POST/PUT must not be replayed
    // (server generates IDs; a committed-then-500 retry would create duplicates)
    const method = (options.method || 'GET').toUpperCase();
    const isIdempotent = method === 'GET' || method === 'HEAD' || method === 'DELETE' || method === 'OPTIONS';
    let lastError;
    let lastResponse;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            const response = await fetchWithTimeout(resource, options, timeoutMs);
            if (response.status >= 500 && attempt < maxRetries && isIdempotent) {
                lastResponse = response;
                await delay(500 * Math.pow(2, attempt));
                continue;
            }
            return response;
        } catch (error) {
            lastError = error;

            // Don't retry on abort (user-initiated or timeout)
            if (error.name === 'AbortError') {
                throw error;
            }

            // Retry on network errors (including QUIC protocol errors)
            if (attempt < maxRetries && (error.name === 'TypeError' || error.message.includes('fetch'))) {
                // Exponential backoff: 500ms, 1000ms
                await delay(500 * Math.pow(2, attempt));
                continue;
            }

            throw error;
        }
    }

    return lastResponse ?? (() => { throw lastError || new Error('Request failed'); })();
}

export async function saveData(key, value) {
    return _enqueueWrite(key, value, async (val) => {
        try {
            return await _postStorageValue(key, val);
        } catch (error) {
            console.error(`Error saving data for key "${key}":`, error);
            throw error;
        }
    });
}

export async function deleteData(key) {
    try {
        const response = await fetchWithRetry(`/api/storage?key=${encodeURIComponent(key)}`, {
            method: "DELETE",
            headers: getAuthHeaders(),
        });

        if (!response.ok) {
            if (response.status === 401) {
                window.location.hash = '#login';
                throw new Error('Unauthorized - please login');
            }
            throw new Error(`Failed to delete data: ${response.status} ${response.statusText}`);
        }

        return true;
    } catch (error) {
        console.error(`Error deleting data for key "${key}":`, error);
        throw error;
    }
}

export async function loadData(key) {
    try {
        const res = await fetchWithRetry(`/api/storage?key=${encodeURIComponent(key)}`, {
            headers: getAuthHeaders()
        });

        if (!res.ok) {
            if (res.status === 401) {
                // Unauthorized - redirect to login
                window.location.hash = '#login';
                return null;
            }
            if (res.status === 404) {
                return null; // Key doesn't exist yet
            }
            throw new Error(`Failed to load data: ${res.status} ${res.statusText}`);
        }

        const text = await res.text();
        return text && text !== "null" ? JSON.parse(text) : null;
    } catch (error) {
        console.error(`Error loading data for key "${key}":`, error);
        // Return null on error to allow app to continue with empty state
        return null;
    }
}

export async function loadManyData(keys) {
    try {
        const list = Array.isArray(keys) ? keys : [];
        const qs = encodeURIComponent(list.join(','));
        const res = await fetchWithRetry(`/api/storage/batch?keys=${qs}`, {
            headers: getAuthHeaders()
        });

        if (!res.ok) {
            if (res.status === 401) {
                window.location.hash = '#login';
                return null;
            }
            return null;
        }

        const data = await res.json();
        return (data && typeof data === 'object') ? data : null;
    } catch (error) {
        console.error('Error batch loading data:', error);
        return null;
    }
}

export async function loadFeedbackIndex() {
    return loadData("feedback:index");
}

export async function saveFeedbackIndex(ids) {
    return saveData("feedback:index", ids);
}

export async function loadFeedbackItem(id) {
    return loadData(`feedback:item:${id}`);
}

export async function saveFeedbackItem(item) {
    return saveData(`feedback:item:${item.id}`, item);
}

export async function deleteFeedbackItem(id) {
    return deleteData(`feedback:item:${id}`);
}

export async function saveFeedbackDelta(delta) {
    try {
        const response = await fetchWithRetry(`/api/storage?key=feedbackItems&op=delta`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(delta),
        });

        if (!response.ok) {
            if (response.status === 401) {
                window.location.hash = '#login';
                throw new Error('Unauthorized - please login');
            }
            throw new Error(`Failed to save feedback delta: ${response.status} ${response.statusText}`);
        }

        return true;
    } catch (error) {
        console.error('Error saving feedback delta:', error);
        throw error;
    }
}

/**
 * Batch process multiple feedback operations in a single API call.
 * This significantly improves performance by reducing N API calls to 1.
 *
 * @param {Array} operations - Array of operations: [{action: 'add'|'update'|'delete', item?: object, id?: number}, ...]
 * @param {number} timeoutMs - Optional timeout in milliseconds (default: 20000)
 * @returns {Promise<object>} Response containing success status, processed count, and updated index
 */
export async function batchFeedbackOperations(operations, timeoutMs = DEFAULT_TIMEOUT_MS) {
    try {
        const debugEnabled = isDebugLogsEnabled();
        const requestId = `fb-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const startedAt = (typeof performance !== "undefined" && performance.now)
            ? performance.now()
            : Date.now();
        let payloadBytes = null;
        if (debugEnabled) {
            try {
                payloadBytes = JSON.stringify({ operations }).length;
            } catch (e) {}
        }
        const headers = getAuthHeaders();
        if (debugEnabled) {
            headers["X-Debug-Logs"] = "1";
            headers["X-Request-Id"] = requestId;
            logFeedbackDebug("batch-feedback:request", {
                requestId,
                operationCount: Array.isArray(operations) ? operations.length : 0,
                payloadBytes,
                timeoutMs
            });
        }

        const response = await fetchWithRetry(`/api/batch-feedback`, {
            method: "POST",
            headers: headers,
            body: JSON.stringify({ operations }),
        }, timeoutMs);

        if (!response.ok) {
            if (response.status === 401) {
                window.location.hash = '#login';
                throw new Error('Unauthorized - please login');
            }
            throw new Error(`Failed to batch process feedback: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        if (debugEnabled) {
            const endedAt = (typeof performance !== "undefined" && performance.now)
                ? performance.now()
                : Date.now();
            logFeedbackDebug("batch-feedback:response", {
                requestId,
                durationMs: Math.round(endedAt - startedAt),
                success: data && data.success,
                processed: data && data.processed,
                total: data && data.total
            });
        }
        return data;
    } catch (error) {
        console.error('Error batch processing feedback:', error);
        throw error;
    }
}

async function parseJsonResponse(response, fallbackMessage) {
    let data = null;
    try {
        data = await response.json();
    } catch (error) {
        data = null;
    }

    if (!response.ok) {
        if (response.status === 401) {
            window.location.hash = '#login';
            const error = new Error('Unauthorized - please login');
            error.status = 401;
            throw error;
        }

        const message = data?.error?.message || data?.error || data?.message || fallbackMessage;
        const error = new Error(message);
        error.status = response.status;
        error.payload = data;
        throw error;
    }

    return data;
}

export async function listProjectDocs() {
    const response = await fetchWithRetry('/api/docs', {
        headers: getAuthHeaders()
    });
    return parseJsonResponse(response, 'Failed to load docs');
}

export async function loadProjectDoc(docId) {
    const response = await fetchWithRetry(`/api/docs?id=${encodeURIComponent(docId)}`, {
        headers: getAuthHeaders()
    });
    return parseJsonResponse(response, 'Failed to load doc page');
}

export async function createProjectDoc(payload) {
    const response = await fetchWithRetry('/api/docs', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseJsonResponse(response, 'Failed to create doc page');
}

export async function updateProjectDoc(docId, payload) {
    const response = await fetchWithRetry(`/api/docs?id=${encodeURIComponent(docId)}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseJsonResponse(response, 'Failed to update doc page');
}

export async function deleteProjectDoc(docId) {
    const response = await fetchWithRetry(`/api/docs?id=${encodeURIComponent(docId)}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
    });
    return parseJsonResponse(response, 'Failed to delete doc page');
}

export async function listTrashedProjectDocs() {
    const response = await fetchWithRetry('/api/docs?action=trash', {
        headers: getAuthHeaders()
    });
    return parseJsonResponse(response, 'Failed to load trashed docs');
}

export async function restoreTrashedProjectDoc(docId) {
    const response = await fetchWithRetry(`/api/docs?id=${encodeURIComponent(docId)}&action=restoreTrash`, {
        method: 'POST',
        headers: getAuthHeaders()
    });
    return parseJsonResponse(response, 'Failed to restore doc page');
}

export async function permanentlyDeleteTrashedProjectDoc(docId) {
    const response = await fetchWithRetry(`/api/docs?id=${encodeURIComponent(docId)}&action=purgeTrash`, {
        method: 'DELETE',
        headers: getAuthHeaders()
    });
    return parseJsonResponse(response, 'Failed to permanently delete doc page');
}

export async function emptyTrashedProjectDocs() {
    const response = await fetchWithRetry('/api/docs?action=purgeTrashAll', {
        method: 'DELETE',
        headers: getAuthHeaders()
    });
    return parseJsonResponse(response, 'Failed to empty docs trash');
}

// Doc-templates API uses a self-contained fetch path that NEVER touches
// window.location on any status (including 401). This is critical: the shared
// parseJsonResponse() helper redirects to #login on 401 as a side effect,
// which is destructive for this feature where a missing table or token issue
// must surface as a normal error toast, not a logout.
async function _docTemplatesRequest(url, init, errorPrefix) {
    let response;
    try {
        response = await fetch(url, init);
    } catch (networkError) {
        throw new Error(`${errorPrefix}: network error`);
    }
    let bodyText = '';
    try { bodyText = await response.text(); } catch (_) {}
    if (!response.ok) {
        let message = `${errorPrefix} (${response.status})`;
        try {
            const parsed = bodyText ? JSON.parse(bodyText) : null;
            if (parsed) {
                const detail = parsed.message || parsed.error;
                if (detail) {
                    message = parsed.error && parsed.message
                        ? `${errorPrefix}: ${parsed.error} — ${parsed.message}`
                        : `${errorPrefix}: ${detail}`;
                }
            }
        } catch (_) {
            if (bodyText) message = `${errorPrefix}: ${bodyText.slice(0, 200)}`;
        }
        throw new Error(message);
    }
    try {
        return bodyText ? JSON.parse(bodyText) : {};
    } catch (_) {
        return {};
    }
}

export async function getDocTemplates() {
    return _docTemplatesRequest('/api/doc-templates', {
        method: 'GET',
        headers: getAuthHeaders()
    }, 'Failed to load doc templates');
}

export async function saveDocTemplate(payload) {
    return _docTemplatesRequest('/api/doc-templates', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    }, 'Failed to save doc template');
}

export async function deleteDocTemplate(templateId) {
    return _docTemplatesRequest(`/api/doc-templates?id=${encodeURIComponent(templateId)}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
    }, 'Failed to delete doc template');
}

export async function updateProjectDocTree(docId, payload) {
    const response = await fetchWithRetry(`/api/docs?id=${encodeURIComponent(docId)}&action=tree`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseJsonResponse(response, 'Failed to update doc tree');
}

export async function publishProjectDoc(docId, payload = {}) {
    // maxRetries=0: a publish/revoke that "failed" client-side may still have
    // committed server-side; replaying it can re-activate a just-revoked link.
    const response = await fetchWithRetry(`/api/docs?id=${encodeURIComponent(docId)}&action=publish`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    }, undefined, 0);
    return parseJsonResponse(response, 'Failed to publish doc page');
}

export async function repairDocPageFiles(docId) {
    const response = await fetchWithRetry(`/api/docs?id=${encodeURIComponent(docId)}&action=repairFiles`, {
        method: 'POST',
        headers: getAuthHeaders()
    });
    return parseJsonResponse(response, 'Failed to repair doc page files');
}

export async function loadPublishedProjectDoc(publicToken) {
    const response = await fetchWithRetry(`/api/docs?publicToken=${encodeURIComponent(publicToken)}`);
    return parseJsonResponse(response, 'Failed to load published doc page');
}

export async function savePublicDocCheckbox(publicToken, contentJson) {
    const response = await fetchWithRetry(
        `/api/docs?publicToken=${encodeURIComponent(publicToken)}&action=checkbox`,
        {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ contentJson })
        }
    );
    if (!response.ok) {
        console.warn('[docs] public checkbox save failed', response.status);
    }
}

export async function recordDocSaveEvent(payload) {
    const response = await fetchWithRetry('/api/docs?action=saveEvent', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload || {})
    });
    return parseJsonResponse(response, 'Failed to record doc save event');
}

export async function listProjectDocHistory(docId) {
    const response = await fetchWithRetry(`/api/docs?id=${encodeURIComponent(docId)}&action=history`, {
        headers: getAuthHeaders()
    });
    return parseJsonResponse(response, 'Failed to load doc history');
}

export async function loadProjectDocRevision(docId, revisionId) {
    const response = await fetchWithRetry(`/api/docs?id=${encodeURIComponent(docId)}&action=revision&revisionId=${encodeURIComponent(revisionId)}`, {
        headers: getAuthHeaders()
    });
    return parseJsonResponse(response, 'Failed to load doc revision');
}

export async function restoreProjectDocRevision(docId, revisionId) {
    const response = await fetchWithRetry(`/api/docs?id=${encodeURIComponent(docId)}&action=restore`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ revisionId })
    });
    return parseJsonResponse(response, 'Failed to restore doc revision');
}

export async function askKaiGenerate(action, payload, options = {}) {
    const headers = getAuthHeaders();
    if (isDebugLogsEnabled()) {
        headers['X-Debug-Logs'] = '1';
    }
    const response = await fetchWithRetry('/api/ask-kai', {
        method: 'POST',
        headers,
        body: JSON.stringify({ action, payload }),
        signal: options.signal
    }, ASK_KAI_TIMEOUT_MS);
    return parseJsonResponse(response, 'Failed to generate Ask Kai proposal');
}

// ── D1 typed helpers (tasks + projects + user data) ──────────────────────────
// These mirror the write-queue + health-reporter pattern used by saveData() but
// target the new D1-backed endpoints instead of /api/storage (KV).
// All console output is gated by the debugLogsEnabled setting (client-side) and
// the X-Debug-Logs: 1 request header (server-side), following the same pattern
// as batchFeedbackOperations.

function logD1Debug(label, meta) {
    if (!isDebugLogsEnabled()) return;
    if (meta) {
        console.log(`[d1] ${label}`, meta);
    } else {
        console.log(`[d1] ${label}`);
    }
}

function d1Headers() {
    const headers = getAuthHeaders();
    if (isDebugLogsEnabled()) {
        headers['X-Debug-Logs'] = '1';
    }
    return headers;
}

function nowMs() {
    return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
}

/**
 * POST the full tasks array to D1 (used by saveTasks in storage.js).
 * Runs through the write queue with key "d1:tasks" so rapid consecutive saves
 * are coalesced exactly like the KV path.
 */
export async function batchSaveTasks(tasks) {
    return _enqueueWrite('d1:tasks', tasks, async (val) => {
        const t0 = nowMs();
        logD1Debug('tasks:batch:request', { count: Array.isArray(val) ? val.length : 0 });

        const response = await fetchWithRetry('/api/tasks?action=batch', {
            method: 'POST',
            headers: d1Headers(),
            body: JSON.stringify(val),
        });
        if (response.ok) {
            reportSaveSuccess();
            logD1Debug('tasks:batch:ok', { count: Array.isArray(val) ? val.length : 0, durationMs: Math.round(nowMs() - t0) });
            return true;
        }
        if (response.status === 401) {
            window.location.hash = '#login';
            throw new Error('Unauthorized - please login');
        }
        // 409 = server refused empty batch to protect against accidental wipe. Non-fatal.
        if (response.status === 409) {
            logD1Debug('tasks:batch:skipped', { reason: 'empty batch refused by server safety guard' });
            return true;
        }
        reportSaveFailure();
        const text = await response.text().catch(() => '');
        throw new Error(`batchSaveTasks failed: ${response.status} ${text}`);
    });
}

/**
 * POST the full projects array to D1 (used by saveProjects in storage.js).
 */
export async function batchSaveProjects(projects) {
    return _enqueueWrite('d1:projects', projects, async (val) => {
        const t0 = nowMs();
        logD1Debug('projects:batch:request', { count: Array.isArray(val) ? val.length : 0 });

        const response = await fetchWithRetry('/api/projects?action=batch', {
            method: 'POST',
            headers: d1Headers(),
            body: JSON.stringify(val),
        });
        if (response.ok) {
            reportSaveSuccess();
            logD1Debug('projects:batch:ok', { count: Array.isArray(val) ? val.length : 0, durationMs: Math.round(nowMs() - t0) });
            return true;
        }
        if (response.status === 401) {
            window.location.hash = '#login';
            throw new Error('Unauthorized - please login');
        }
        // 409 = server refused empty batch to protect against accidental wipe. Non-fatal.
        if (response.status === 409) {
            logD1Debug('projects:batch:skipped', { reason: 'empty batch refused by server safety guard' });
            return true;
        }
        reportSaveFailure();
        const text = await response.text().catch(() => '');
        throw new Error(`batchSaveProjects failed: ${response.status} ${text}`);
    });
}

/**
 * GET /api/startup — returns all user data from D1 in one request.
 * Called by loadAll() in storage.js on app startup.
 */
export async function fetchStartupData() {
    const t0 = nowMs();
    logD1Debug('startup:request');

    const response = await fetchWithRetry('/api/startup', {
        headers: d1Headers(),
    });
    if (!response.ok) {
        if (response.status === 401) {
            window.location.hash = '#login';
            throw new Error('Unauthorized - please login');
        }
        const text = await response.text().catch(() => '');
        throw new Error(`fetchStartupData failed: ${response.status} ${text}`);
    }
    const data = await response.json();
    logD1Debug('startup:ok', {
        tasks: Array.isArray(data.tasks) ? data.tasks.length : 0,
        projects: Array.isArray(data.projects) ? data.projects.length : 0,
        migrated: data.migrated,
        durationMs: Math.round(nowMs() - t0)
    });
    return data;
}

/**
 * PUT /api/user-data — update any subset of user settings columns.
 * patch: { settings?, projectColors?, projectColorOverrides?, tagColors?, tagColorOverrides?, tagColorsVersion?, sortMode?, manualTaskOrder?, automations?, savedPrompts? }
 */
export async function saveUserData(patch) {
    return _enqueueWrite('d1:user-data', patch, async (val) => {
        const t0     = nowMs();
        const fields = Object.keys(val || {});
        logD1Debug('user-data:put:request', { fields });

        const response = await fetchWithRetry('/api/user-data', {
            method: 'PUT',
            headers: d1Headers(),
            body: JSON.stringify(val),
        });
        if (response.ok) {
            reportSaveSuccess();
            logD1Debug('user-data:put:ok', { fields, durationMs: Math.round(nowMs() - t0) });
            return true;
        }
        if (response.status === 401) {
            window.location.hash = '#login';
            throw new Error('Unauthorized - please login');
        }
        reportSaveFailure();
        const text = await response.text().catch(() => '');
        throw new Error(`saveUserData failed: ${response.status} ${text}`);
    });
}

/**
 * POST /api/tasks?action=dependencies — full replace of the dependencies map.
 */
export async function saveDependencies(depsObj) {
    return _enqueueWrite('d1:dependencies', depsObj, async (val) => {
        const t0    = nowMs();
        const count = Object.keys(val || {}).length;
        logD1Debug('dependencies:save:request', { dependentTasks: count });

        const response = await fetchWithRetry('/api/tasks?action=dependencies', {
            method: 'POST',
            headers: d1Headers(),
            body: JSON.stringify(val),
        });
        if (response.ok) {
            reportSaveSuccess();
            logD1Debug('dependencies:save:ok', { dependentTasks: count, durationMs: Math.round(nowMs() - t0) });
            return true;
        }
        if (response.status === 401) {
            window.location.hash = '#login';
            throw new Error('Unauthorized - please login');
        }
        reportSaveFailure();
        const text = await response.text().catch(() => '');
        throw new Error(`saveDependencies failed: ${response.status} ${text}`);
    });
}

/**
 * POST /api/user-data?resource=history — full replace of the history array.
 */
export async function saveHistory(history) {
    return _enqueueWrite('d1:history', history, async (val) => {
        const t0 = nowMs();
        logD1Debug('history:save:request', { count: Array.isArray(val) ? val.length : 0 });

        const response = await fetchWithRetry('/api/user-data?resource=history', {
            method: 'POST',
            headers: d1Headers(),
            body: JSON.stringify(val),
        });
        if (response.ok) {
            reportSaveSuccess();
            logD1Debug('history:save:ok', { count: Array.isArray(val) ? val.length : 0, durationMs: Math.round(nowMs() - t0) });
            return true;
        }
        if (response.status === 401) {
            window.location.hash = '#login';
            throw new Error('Unauthorized - please login');
        }
        reportSaveFailure();
        const text = await response.text().catch(() => '');
        throw new Error(`saveHistory failed: ${response.status} ${text}`);
    });
}

/**
 * POST /api/user-data?resource=templates — full replace of the templates array.
 */
export async function saveTemplatesD1(templates) {
    return _enqueueWrite('d1:templates', templates, async (val) => {
        const t0 = nowMs();
        logD1Debug('templates:save:request', { count: Array.isArray(val) ? val.length : 0 });

        const response = await fetchWithRetry('/api/user-data?resource=templates', {
            method: 'POST',
            headers: d1Headers(),
            body: JSON.stringify(val),
        });
        if (response.ok) {
            reportSaveSuccess();
            logD1Debug('templates:save:ok', { count: Array.isArray(val) ? val.length : 0, durationMs: Math.round(nowMs() - t0) });
            return true;
        }
        if (response.status === 401) {
            window.location.hash = '#login';
            throw new Error('Unauthorized - please login');
        }
        reportSaveFailure();
        const text = await response.text().catch(() => '');
        throw new Error(`saveTemplatesD1 failed: ${response.status} ${text}`);
    });
}

// ═══════════════════════════════════════════════════════════════════════════════
// Row-level D1 Operations (Phase 2)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * POST /api/{tasks|projects}?action=reserveIds — reserve `count` ids for this device.
 * The server hands out disjoint blocks per user, so two devices never pick the same id.
 * @param {'task'|'project'} kind
 * @returns {Promise<{start: number, end: number}>} inclusive range
 */
export async function reserveIdsD1(kind, count) {
    const resource = kind === 'project' ? 'projects' : 'tasks';
    const response = await fetchWithRetry(`/api/${resource}?action=reserveIds`, {
        method: 'POST',
        headers: d1Headers(),
        body: JSON.stringify({ count }),
    }, undefined, 0);
    // Background helper: never navigate (no #login redirect on 401) — the caller just
    // falls back to max(id)+1, and real saves still handle auth themselves.
    const data = await response.json().catch(() => null);
    if (!response.ok || !Number.isFinite(data?.start) || !Number.isFinite(data?.end)) {
        throw new Error(`reserveIdsD1(${kind}) failed: ${response.status}`);
    }
    logD1Debug('ids:reserved', { kind, start: data.start, end: data.end });
    return { start: data.start, end: data.end };
}

/**
 * POST /api/tasks — INSERT one task row.
 * Uses unique queue key to prevent coalescing with updates.
 */
export async function insertTaskD1(task) {
    const queueKey = `d1:task:insert:${task.id}`;
    return _enqueueWrite(queueKey, task, async (val) => {
        const t0 = nowMs();
        logD1Debug('task:insert:request', { id: val.id });

        const response = await fetchWithRetry('/api/tasks', {
            method: 'POST',
            headers: d1Headers(),
            body: JSON.stringify(val),
        });
        if (response.ok) {
            reportSaveSuccess();
            logD1Debug('task:insert:ok', { id: val.id, durationMs: Math.round(nowMs() - t0) });
            return true;
        }
        if (response.status === 401) {
            window.location.hash = '#login';
            throw new Error('Unauthorized - please login');
        }
        if (response.status === 409) {
            // The id belongs to a DIFFERENT task (another device took it). A retry of this
            // same insert is answered 200 by the server, so 409 is always a real failure.
            logD1Debug('task:insert:id-conflict', { id: val.id });
            const conflict = new Error(`insertTaskD1(id=${val.id}) failed: id already used by another task`);
            conflict.code = 'ID_CONFLICT';
            throw conflict;
        }
        reportSaveFailure();
        const text = await response.text().catch(() => '');
        throw new Error(`insertTaskD1(id=${val.id}) failed: ${response.status} ${text}`);
    });
}

/**
 * PUT /api/tasks?id=X — partial UPDATE.
 * Queue key includes id to coalesce rapid updates to same task.
 */
export async function updateTaskD1(id, changes) {
    const queueKey = `d1:task:${id}`;
    return _enqueueWrite(queueKey, changes, async (val) => {
        const t0 = nowMs();
        const fields = Object.keys(val || {});
        logD1Debug('task:update:request', { id, fields });

        const response = await fetchWithRetry(`/api/tasks?id=${id}`, {
            method: 'PUT',
            headers: d1Headers(),
            body: JSON.stringify(val),
        });
        if (response.ok) {
            reportSaveSuccess();
            logD1Debug('task:update:ok', { id, fields, durationMs: Math.round(nowMs() - t0) });
            return true;
        }
        if (response.status === 401) {
            window.location.hash = '#login';
            throw new Error('Unauthorized - please login');
        }
        reportSaveFailure();
        const text = await response.text().catch(() => '');
        throw new Error(`updateTaskD1(id=${id}) failed: ${response.status} ${text}`);
    });
}

/**
 * DELETE /api/tasks?id=X — delete task.
 * Idempotent - 404 response treated as success (already deleted = correct state).
 * No queue needed - always goes through immediately.
 */
export async function deleteTaskD1(id) {
    const t0 = nowMs();
    logD1Debug('task:delete:request', { id });

    const response = await fetchWithRetry(`/api/tasks?id=${id}`, {
        method: 'DELETE',
        headers: d1Headers(),
    });
    if (response.ok) {
        reportSaveSuccess();
        logD1Debug('task:delete:ok', { id, durationMs: Math.round(nowMs() - t0) });
        return true;
    }
    if (response.status === 401) {
        window.location.hash = '#login';
        throw new Error('Unauthorized - please login');
    }
    if (response.status === 404) {
        // Already deleted - treat as success
        logD1Debug('task:delete:not-found', { id });
        return true;
    }
    reportSaveFailure();
    const text = await response.text().catch(() => '');
    throw new Error(`deleteTaskD1(id=${id}) failed: ${response.status} ${text}`);
}

/**
 * POST /api/tasks?action=reorder — batch UPDATE project_order only.
 * Uses single queue key to ensure ordering is preserved.
 */
export async function reorderTasksD1(updates) {
    if (!Array.isArray(updates) || updates.length === 0) {
        return Promise.resolve({ success: true, count: 0 });
    }
    const queueKey = 'd1:tasks:reorder';
    return _enqueueWrite(queueKey, updates, async (val) => {
        const t0 = nowMs();
        logD1Debug('tasks:reorder:request', { count: val.length });

        const response = await fetchWithRetry('/api/tasks?action=reorder', {
            method: 'POST',
            headers: d1Headers(),
            body: JSON.stringify(val),
        });
        if (response.ok) {
            reportSaveSuccess();
            logD1Debug('tasks:reorder:ok', { count: val.length, durationMs: Math.round(nowMs() - t0) });
            return true;
        }
        if (response.status === 401) {
            window.location.hash = '#login';
            throw new Error('Unauthorized - please login');
        }
        reportSaveFailure();
        const text = await response.text().catch(() => '');
        throw new Error(`reorderTasksD1(count=${val.length}) failed: ${response.status} ${text}`);
    });
}

/**
 * POST /api/projects — INSERT one project row.
 * Uses unique queue key to prevent coalescing with updates.
 */
export async function insertProjectD1(project) {
    const queueKey = `d1:project:insert:${project.id}`;
    return _enqueueWrite(queueKey, project, async (val) => {
        const t0 = nowMs();
        logD1Debug('project:insert:request', { id: val.id });

        const response = await fetchWithRetry('/api/projects', {
            method: 'POST',
            headers: d1Headers(),
            body: JSON.stringify(val),
        });
        if (response.ok) {
            reportSaveSuccess();
            logD1Debug('project:insert:ok', { id: val.id, durationMs: Math.round(nowMs() - t0) });
            return true;
        }
        if (response.status === 401) {
            window.location.hash = '#login';
            throw new Error('Unauthorized - please login');
        }
        if (response.status === 409) {
            // The id belongs to a DIFFERENT project (another device took it). A retry of
            // this same insert is answered 200 by the server, so 409 is a real failure.
            logD1Debug('project:insert:id-conflict', { id: val.id });
            const conflict = new Error(`insertProjectD1(id=${val.id}) failed: id already used by another project`);
            conflict.code = 'ID_CONFLICT';
            throw conflict;
        }
        reportSaveFailure();
        const text = await response.text().catch(() => '');
        throw new Error(`insertProjectD1(id=${val.id}) failed: ${response.status} ${text}`);
    });
}

/**
 * PUT /api/projects?id=X — partial UPDATE.
 * Queue key includes id to coalesce rapid updates to same project.
 */
export async function updateProjectD1(id, changes) {
    const queueKey = `d1:project:${id}`;
    return _enqueueWrite(queueKey, changes, async (val) => {
        const t0 = nowMs();
        const fields = Object.keys(val || {});
        logD1Debug('project:update:request', { id, fields });

        const response = await fetchWithRetry(`/api/projects?id=${id}`, {
            method: 'PUT',
            headers: d1Headers(),
            body: JSON.stringify(val),
        });
        if (response.ok) {
            reportSaveSuccess();
            logD1Debug('project:update:ok', { id, fields, durationMs: Math.round(nowMs() - t0) });
            return true;
        }
        if (response.status === 401) {
            window.location.hash = '#login';
            throw new Error('Unauthorized - please login');
        }
        reportSaveFailure();
        const text = await response.text().catch(() => '');
        throw new Error(`updateProjectD1(id=${id}) failed: ${response.status} ${text}`);
    });
}

/**
 * DELETE /api/projects?id=X — delete project.
 * Idempotent - 404 response treated as success (already deleted = correct state).
 * No queue needed - always goes through immediately.
 */
export async function deleteProjectD1(id) {
    const t0 = nowMs();
    logD1Debug('project:delete:request', { id });

    const response = await fetchWithRetry(`/api/projects?id=${id}`, {
        method: 'DELETE',
        headers: d1Headers(),
    });
    if (response.ok) {
        reportSaveSuccess();
        logD1Debug('project:delete:ok', { id, durationMs: Math.round(nowMs() - t0) });
        return true;
    }
    if (response.status === 401) {
        window.location.hash = '#login';
        throw new Error('Unauthorized - please login');
    }
    if (response.status === 404) {
        // Already deleted - treat as success
        logD1Debug('project:delete:not-found', { id });
        return true;
    }
    reportSaveFailure();
    const text = await response.text().catch(() => '');
    throw new Error(`deleteProjectD1(id=${id}) failed: ${response.status} ${text}`);
}
